import React, { useState, useEffect } from 'react';
import { Play, Pause, Square, BookOpen, Quote, Users } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useGroupStore } from '../store/groupStore';
import { useTimerStore } from '../store/timerStore';
import ProgressRing from '../components/ProgressRing';
import { useToast } from '../components/Toast';
import GlassCard from '../components/GlassCard';
import Modal from '../components/Modal';
import { AVATARS, formatTime, MOTIVATIONAL_QUOTES } from '../lib/utils';
import { groupsApi } from '../lib/apiClient';

export default function StudyPage() {
  const { user } = useAuthStore();
  const { saveSession } = useGroupStore();
  const toast = useToast();
  
  const {
    isRunning,
    isPaused,
    topic,
    elapsedSeconds,
    setTopic,
    start,
    pause,
    stop
  } = useTimerStore();

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [sessionNote, setSessionNote] = useState('');
  const [stoppedSessionData, setStoppedSessionData] = useState(null);
  
  // Motivational Quotes State
  const [currentQuote, setCurrentQuote] = useState(MOTIVATIONAL_QUOTES[0]);

  // Live Group Members State
  const [liveMembers, setLiveMembers] = useState([]);

  // Calculate daily goal in seconds (e.g. 1/7 of weekly goal, default 3 hours)
  const weeklyGoalHours = user?.weeklyGoalHours || 21;
  const dailyGoalSeconds = (weeklyGoalHours / 7) * 3600;
  const progressPercent = Math.min((elapsedSeconds / dailyGoalSeconds) * 100, 100);

  // Cycle quotes every 5 minutes
  useEffect(() => {
    const quoteInterval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length);
      setCurrentQuote(MOTIVATIONAL_QUOTES[randomIndex]);
    }, 5 * 60 * 1000);

    return () => clearInterval(quoteInterval);
  }, []);

  // Poll online/studying group members every 30 seconds
  useEffect(() => {
    let active = true;
    const fetchLiveMembers = async () => {
      if (!user?.groupId) return;
      try {
        // TODO: connect to real API — GET /api/groups/:id/live
        const res = await groupsApi.getLive(user.groupId).catch(() => null);
        if (active && res) {
          setLiveMembers(res);
        } else if (active) {
          setLiveMembers([]);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchLiveMembers();
    const liveInterval = setInterval(fetchLiveMembers, 30 * 1000);

    return () => {
      active = false;
      clearInterval(liveInterval);
    };
  }, [user?.groupId]);

  const handleStart = () => {
    if (!topic.trim()) {
      toast.error('A study topic is required before starting the session!');
      return;
    }
    start();
    toast.success(`Session started on: ${topic}`);
  };

  const handlePause = () => {
    pause();
    toast.info('Session paused');
  };

  const handleStop = () => {
    const data = stop();
    setStoppedSessionData(data);
    setSessionNote('');
    setShowNoteModal(true);
  };

  const saveStoppedSession = async () => {
    if (!stoppedSessionData) return;
    
    const finalSession = {
      topic: stoppedSessionData.topic,
      durationSec: stoppedSessionData.duration,
      note: sessionNote.trim() || null,
      startedAt: Math.floor((Date.now() - stoppedSessionData.duration * 1000) / 1000),
      endedAt: Math.floor(Date.now() / 1000)
    };

    try {
      await saveSession(finalSession);
      
      toast.success('Study session saved successfully!');
    } catch (e) {
      toast.error('Failed to save study session.');
    } finally {
      setShowNoteModal(false);
      setStoppedSessionData(null);
    }
  };

  return (
    <div className="flex flex-col h-full p-6 md:p-8 overflow-y-auto space-y-6">
      
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h1 className="heading text-3xl font-extrabold text-white tracking-wide">
          Study Sanctum
        </h1>
        <p className="text-white/40 text-sm">
          Focus is a silent covenant. Dedicate your hours, rise in the ranks.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Timer Container (Left/Center Column) */}
        <div className="lg:col-span-2 flex flex-col items-center justify-center p-6 rounded-2xl bg-white/5 border border-white/10 relative overflow-hidden backdrop-blur-md">
          <div className="absolute inset-0 bg-indigo-500/5 filter blur-3xl pointer-events-none -z-10" />

          {/* Topic Selector */}
          <div className="w-full max-w-sm mb-8 flex flex-col gap-2">
            <label className="text-white/60 text-xs font-semibold uppercase tracking-wider">
              Current Endeavor
            </label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={isRunning || isPaused}
              placeholder="What knowledge are you pursuing?"
              className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none transition-all duration-200 border border-white/10"
              style={{
                background: 'rgba(255,255,255,0.03)',
                borderColor: (isRunning || isPaused) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)'
              }}
            />
          </div>

          {/* Progress Ring with Timer inside */}
          <div className="relative my-4 flex items-center justify-center">
            <ProgressRing
              progress={progressPercent}
              size={280}
              strokeWidth={14}
              color="#6366f1"
              pulse={isRunning}
            >
              <div className="text-center flex flex-col items-center select-all">
                <span className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-1">
                  {isPaused ? 'PAUSED' : isRunning ? 'STUDYING' : 'IDLE'}
                </span>
                <span className="heading text-4xl font-black text-white font-mono tracking-tight">
                  {formatTime(elapsedSeconds)}
                </span>
                <span className="text-white/30 text-[10px] mt-1 tracking-wider uppercase font-semibold">
                  Goal: {Math.round(weeklyGoalHours / 7)} hr/day
                </span>
              </div>
            </ProgressRing>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4 mt-8">
            {/* Start Button */}
            {!isRunning ? (
              <button
                onClick={handleStart}
                className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #22c55e, #15803d)',
                  boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4), 0 0 16px rgba(34, 197, 94, 0.2)'
                }}
              >
                <Play size={22} className="text-white fill-current ml-1" />
              </button>
            ) : (
              /* Pause Button */
              <button
                onClick={handlePause}
                className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #b45309)',
                  boxShadow: '0 4px 20px rgba(245, 158, 11, 0.4)'
                }}
              >
                <Pause size={22} className="text-white fill-current" />
              </button>
            )}

            {/* Stop Button */}
            {(isRunning || isPaused) && (
              <button
                onClick={handleStop}
                className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                  boxShadow: '0 4px 20px rgba(239, 68, 68, 0.4)'
                }}
              >
                <Square size={20} className="text-white fill-current" />
              </button>
            )}
          </div>
        </div>

        {/* Info & Side Panel (Right Column) */}
        <div className="space-y-6">
          
          {/* Quote Card */}
          <GlassCard className="p-5 flex flex-col gap-4 border-l-4 border-l-indigo-500">
            <div className="flex items-center gap-2 text-indigo-400">
              <Quote size={18} />
              <span className="text-xs uppercase tracking-widest font-semibold">Tome of Wisdom</span>
            </div>
            <div className="flex flex-col gap-2">
              <p className="heading text-white/90 text-sm italic leading-relaxed">
                "{currentQuote.text}"
              </p>
              <span className="text-white/30 text-xs self-end font-semibold tracking-wider">
                — {currentQuote.author}
              </span>
            </div>
          </GlassCard>

          {/* Online/Studying Group Members */}
          <GlassCard className="p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2 text-green-400">
              <Users size={16} />
              <span className="text-xs uppercase tracking-widest font-semibold">The Circle Studies</span>
            </div>
            {liveMembers.length === 0 ? (
              <div className="text-white/20 text-xs py-4 text-center">
                Your group members are currently offline or resting.
              </div>
            ) : (
              <div className="space-y-3">
                {liveMembers.map(m => {
                  const avatarColor = AVATARS[m.avatar % AVATARS.length]?.bg || '#6366f1';
                  const avatarEmoji = AVATARS[m.avatar % AVATARS.length]?.emoji || '🎓';
                  
                  return (
                    <div key={m.id} className="flex items-center justify-between p-2 rounded-xl bg-white/5 border border-white/5">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm shadow-inner"
                          style={{ background: avatarColor }}
                        >
                          {avatarEmoji}
                        </div>
                        <span className="text-white/80 text-xs font-semibold">{m.name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-green-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-ping" />
                        <span className="text-xs font-mono font-bold tracking-tight">
                          {formatTime(m.elapsed_sec)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        </div>
      </div>

      {/* Note Modal */}
      <Modal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)}>
        <div className="flex flex-col gap-4 p-2">
          <div className="flex items-center gap-2.5 text-indigo-400 border-b border-white/5 pb-2">
            <BookOpen size={20} />
            <h2 className="heading text-xl font-bold text-white">Record Study Progress</h2>
          </div>
          <p className="text-white/40 text-xs">
            Would you care to log an academic note about your study session on <strong className="text-indigo-400 font-bold">{stoppedSessionData?.topic}</strong>?
          </p>

          <textarea
            value={sessionNote}
            onChange={(e) => setSessionNote(e.target.value)}
            placeholder="Document your findings, equations solved, or pages read..."
            rows={4}
            className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-indigo-500/60 focus:bg-white/10 transition-all duration-200"
          />

          <div className="flex items-center justify-end gap-3 mt-2">
            <button
              onClick={() => {
                setShowNoteModal(false);
                saveStoppedSession();
              }}
              className="px-4 py-2 rounded-xl text-white/50 hover:text-white hover:bg-white/5 text-xs transition-all duration-200 font-semibold"
            >
              Skip Note
            </button>
            <button
              onClick={saveStoppedSession}
              className="btn-primary px-5 py-2.5 text-xs rounded-xl font-bold glow-indigo"
            >
              Save Chronicle
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
